import type { HelpGuide } from '../types';

export const reconGuide: HelpGuide = {
  key: 'recon',
  title: 'กระทบยอดธนาคาร (Bank Reconciliation)',
  subtitle: 'เมนู: รายงานและภาษี ▸ กระทบยอดธนาคาร',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/recon/คู่มือการใช้งาน_กระทบยอดธนาคาร_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “กระทบยอดเงินฝากธนาคาร” ใช้เปรียบเทียบยอดเงินฝากตาม Bank Statement (รายการเดินบัญชีจากธนาคาร) กับใบสำคัญบัญชีเงินฝากที่บันทึกในระบบ เพื่อหาผลต่างและปรับปรุงให้ตรงกันก่อนปิดงวด รองรับทุกบัญชีธนาคารของบริษัท พร้อมจับคู่รายการอัตโนมัติและบันทึกรายการปรับปรุง (ค่าธรรมเนียม/ดอกเบี้ย/เช็คค้าง)',
        },
        {
          kind: 'callout',
          text: 'หลักการกระทบยอด: นำเข้า Bank Statement (ยอดยกมา + เคลื่อนไหว = ยอดปิด) → จับคู่รายการ statement กับใบสำคัญในระบบ (อัตโนมัติ/ปรับปรุง) → เมื่อทุกรายการเคลียร์และสมการยอดตรง จึงปิดกระทบยอด (ล็อกเป็น RECONCILED)',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ภาพรวมทุกบัญชี — ',
              text: 'รายการบัญชีธนาคารทั้งหมด + สถานะกระทบยอด + KPI (แท็บเริ่มต้น)',
            },
            {
              lead: 'Match Workspace · จับคู่รายการ — ',
              text: 'หน้าจอจับคู่รายการ statement ทีละบรรทัดของบัญชีที่เลือก',
            },
            {
              lead: 'Reconciliation Statement — ',
              text: 'งบกระทบยอดละเอียด: ยอดยกมา+เคลื่อนไหว=ยอดปิด + รายการปรับปรุง + ร่องรอยการตรวจสอบ',
            },
            {
              lead: 'ประวัติการปิดงวด — ',
              text: 'รายการ statement ที่ปิดกระทบยอด (RECONCILED) แล้วทุกบัญชี',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/recon/01-overview.png',
          caption:
            'ภาพรวมทุกบัญชี — KPI (เงินฝากรวม/กระทบยอดสมบูรณ์/รายการรอจับคู่/บัญชียังไม่ Import) และตารางสถานะกระทบยอดทุกบัญชี',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบนสุด:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เงินฝากธนาคารรวม (ยอดปิด Statement) — ',
              text: 'ผลรวมยอดปิดล่าสุดของทุกบัญชี พร้อมจำนวนบัญชี',
            },
            {
              lead: 'กระทบยอดสมบูรณ์ — ',
              text: 'จำนวนบัญชีที่กระทบยอดเสร็จ / บัญชีทั้งหมด (% ของบัญชี)',
            },
            {
              lead: 'รายการรอจับคู่ — ',
              text: 'จำนวนรายการที่ยังไม่จับคู่ (OPEN) รวมทุกบัญชี',
            },
            {
              lead: 'บัญชีที่ยังไม่ Import — ',
              text: 'จำนวนบัญชีที่ยังไม่ได้นำเข้า Bank Statement',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: นำเข้า Bank Statement · Auto-match · ปิดยอด ตารางภาพรวมแสดงคอลัมน์: ธนาคาร · เลขที่บัญชี · ชื่อบัญชี · ประเภท · ยอดปิด Statement · จับคู่แล้ว · รอจับคู่ · Recon ล่าสุด · สถานะ · การกระทำ — คลิกที่แถว (หรือปุ่ม “กระทบยอด →”) เพื่อเลือกบัญชีและไปยังแท็บ Match Workspace',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'ถ้ายังไม่มีบัญชีธนาคารในระบบ ให้เพิ่มก่อนที่ Master Data › ธนาคารและบัญชี หน้านี้จะแสดงข้อความแจ้งเมื่อไม่มีบัญชี',
        },
      ],
    },
    {
      id: 'import',
      heading: '3. การนำเข้า Bank Statement',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม นำเข้า Bank Statement แล้วกรอก: เลือกบัญชีธนาคาร · วันที่ statement · ยอดยกมา · ยอดปิด และเลือกไฟล์ (.csv/.xlsx/.xls) ระบบมีปุ่ม ดาวน์โหลดเทมเพลต ให้จัดรูปแบบไฟล์ก่อนนำเข้า',
        },
        {
          kind: 'para',
          text: 'หลังเลือกไฟล์ ระบบจะอ่านรายการและแสดงจำนวนบรรทัด + เคลื่อนไหวรวม พร้อมกล่องตรวจสมการยอด: สีเขียวเมื่อ ยอดยกมา + เคลื่อนไหว = ยอดปิด (สมการตรง) หรือสีส้มเตือนเมื่อไม่เท่ากัน หากไฟล์มีปัญหาจะแสดงรายการ error (เช่น แถวที่ขาดวันที่/รายละเอียด) ติ๊ก Run Auto-match ทันทีหลังนำเข้า (ติ๊กไว้ให้แล้ว) เพื่อให้ระบบจับคู่อัตโนมัติทันที แล้วกด นำเข้า',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'ควรตรวจให้กล่องสมการยอดเป็นสีเขียว (สมการตรง) ก่อนกดนำเข้า เพื่อให้กระทบยอดได้สำเร็จในภายหลัง',
        },
        {
          kind: 'image',
          src: '/help/recon/02-import.png',
          caption:
            'นำเข้า Bank Statement — เลือกบัญชี/วันที่/ยอดยกมา-ยอดปิด, เลือกไฟล์ และกล่องตรวจสมการยอด',
        },
      ],
    },
    {
      id: 'match',
      heading: '4. Match Workspace — จับคู่รายการ',
      blocks: [
        {
          kind: 'para',
          text: 'หลังเลือกบัญชีจากแท็บภาพรวม (หรือกดปุ่ม “กระทบยอด →”) ระบบจะพาเข้าแท็บ Match Workspace · จับคู่รายการ ส่วนหัวแสดงธนาคาร/บัญชี/งวด มีปุ่ม Auto-match และ + เพิ่มรายการปรับปรุง',
        },
        {
          kind: 'para',
          text: 'แผงด้านขวาคือรายการจาก Bank Statement แต่ละบรรทัด คอลัมน์: วันที่ · รายการในธนาคาร · ref · จำนวน (สีเขียว = เงินเข้า, สีแดง = เงินออก) · สถานะ แต่ละบรรทัดมีสถานะ OPEN (รอจับคู่) / MATCHED (จับคู่แล้ว) / ADJUSTED (ปรับปรุงแล้ว) แผงด้านซ้าย (ใบสำคัญในระบบ) เป็นการจับคู่ด้วยมือซึ่งอยู่ในเฟสถัดไป',
        },
        {
          kind: 'image',
          src: '/help/recon/03-match-workspace.png',
          caption:
            'Match Workspace — รายการ statement ทีละบรรทัดพร้อมสถานะ OPEN / MATCHED / ADJUSTED',
        },
      ],
    },
    {
      id: 'automatch',
      heading: '5. การจับคู่อัตโนมัติ (Auto-match)',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม Auto-match เพื่อให้ระบบจับคู่รายการ statement กับใบสำคัญบัญชีเงินฝากอัตโนมัติ กล่องเกณฑ์การจับคู่ระบุว่า: ระบบจับคู่รายการที่ จำนวนเงินตรงกัน และ วันที่ห่างกันไม่เกิน ±3 วัน กับใบสำคัญที่ POSTED แล้ว ส่วนรายการที่กำกวม (มีผู้สมัครหลายตัวระยะวันเท่ากัน) จะถูกข้ามไว้ให้จับคู่เอง',
        },
        {
          kind: 'para',
          text: 'เมื่อประมวลผลเสร็จ กล่องผลลัพธ์ (สีเขียว) จะแสดง จับคู่สำเร็จ N รายการ พร้อมจำนวนที่เหลือและจำนวนผู้สมัครที่พิจารณา กด เริ่ม Auto-match เพื่อรัน หรือ ปิด เพื่อออกจากหน้าต่าง',
        },
        {
          kind: 'image',
          src: '/help/recon/04-automatch.png',
          caption: 'Auto-match — เกณฑ์การจับคู่ (จำนวนตรง + วันที่ ±3 วัน) และกล่องผลลัพธ์',
        },
      ],
    },
    {
      id: 'adjustment',
      heading: '6. การเพิ่มรายการปรับปรุง',
      blocks: [
        {
          kind: 'para',
          text: 'รายการในธนาคารที่ไม่มีคู่ในระบบ (เช่น ค่าธรรมเนียม ดอกเบี้ยรับ เช็คที่ยังไม่ขึ้นเงิน) ให้กดปุ่ม + เพิ่มรายการปรับปรุง ใน Match Workspace แล้วเลือก ประเภท · กรอก จำนวน · คำอธิบาย · สาขา/แผนก (ถ้าจำเป็น) สำหรับประเภท ผลต่างอัตราแลกเปลี่ยน และ อื่นๆ จะมีช่อง ทิศทาง ให้เลือกเพิ่มยอด (+) หรือลดยอด (−) แล้วกด บันทึกปรับปรุง',
        },
        {
          kind: 'para',
          text: 'ประเภทที่ลงใบสำคัญ (JV) ให้อัตโนมัติ ได้แก่ ค่าธรรมเนียมธนาคาร · ดอกเบี้ยรับ · ผลต่างอัตราแลกเปลี่ยน ส่วนประเภท เช็คยังไม่ขึ้นเงิน · เงินฝากระหว่างทาง · อื่นๆ เป็นรายการกระทบยอดฝั่งสมุดบัญชีเท่านั้น (ไม่ลง JV)',
        },
        {
          kind: 'image',
          src: '/help/recon/05-add-adjustment.png',
          caption:
            'เพิ่มรายการปรับปรุง — เลือกประเภท (ค่าธรรมเนียม/ดอกเบี้ย/เช็ค ฯลฯ), จำนวน, คำอธิบาย และสาขา/แผนก',
        },
      ],
    },
    {
      id: 'statement',
      heading: '7. งบกระทบยอด (Reconciliation Statement)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Reconciliation Statement แสดงงบกระทบยอดละเอียดของบัญชีที่เลือก การ์ดสมการยอดแสดง ยอดยกมา + เคลื่อนไหว (รับ−จ่าย) = ยอดปิด พร้อมสถานะ “ตรง” (✓ สีเขียว) หรือ “ไม่ตรง” (✗ สีแดง)',
        },
        {
          kind: 'para',
          text: 'ตารางรายการปรับปรุงแสดง ประเภท · คำอธิบาย · จำนวน · JV (“ลง JV แล้ว” ถ้ามีการลงใบสำคัญ) ส่วนท้ายแสดงร่องรอยการตรวจสอบ: สรุปจำนวนรายการ (จับคู่แล้ว/ปรับปรุงแล้ว/ค้าง) · ผู้จัดทำ/นำเข้า · ผู้ปิดกระทบยอด พร้อมวันที่',
        },
        {
          kind: 'image',
          src: '/help/recon/06-statement.png',
          caption:
            'งบกระทบยอด — ยอดยกมา + เคลื่อนไหว = ยอดปิด, ตารางรายการปรับปรุง และร่องรอยการตรวจสอบ',
        },
      ],
    },
    {
      id: 'finalize',
      heading: '8. การปิดกระทบยอด (Finalize)',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อตรวจสอบครบแล้ว กดปุ่ม ปิดยอด เพื่อล็อก statement หน้าต่าง ปิดกระทบยอด statement จะตรวจความพร้อม 3 ข้อก่อนล็อก:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ทุกรายการเคลียร์แล้ว — ',
              text: 'ไม่มีรายการค้าง (OPEN) เหลืออยู่ ต้องจับคู่หรือปรับปรุงให้ครบก่อน',
            },
            {
              lead: 'สมการยอดตรง — ',
              text: 'ยอดยกมา + เคลื่อนไหว = ยอดปิด',
            },
            {
              lead: 'สถานะ DRAFT พร้อมปิด — ',
              text: 'statement นี้ยังไม่เคยปิด (ยังไม่เป็น RECONCILED)',
            },
          ],
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'หลังปิด statement จะถูกล็อกเป็น RECONCILED · รายการปรับปรุง (JV) ถูกลงตอนเพิ่มไปแล้ว — การปิดไม่สร้าง JV เพิ่ม',
        },
        {
          kind: 'para',
          text: 'เมื่อครบทั้ง 3 ข้อ (✓) กด ปิดกระทบยอด statement นี้ ปุ่ม Preview รายงาน อยู่ในเฟสถัดไป',
        },
        {
          kind: 'image',
          src: '/help/recon/07-finalize.png',
          caption:
            'ปิดกระทบยอด statement — รายการตรวจความพร้อม 3 ข้อ (เคลียร์ครบ/สมการตรง/สถานะ DRAFT) ก่อนล็อก',
        },
      ],
    },
    {
      id: 'history',
      heading: '9. ประวัติการปิดงวด',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ประวัติการปิดงวด แสดงรายการ statement ที่ปิดกระทบยอด (RECONCILED) แล้วของทุกบัญชี เป็นแบบอ่านอย่างเดียว คอลัมน์: งวด (MM/YYYY) · วันที่ปิด · บัญชี · ยอดปิด · สถานะ (Reconciled · Locked) · ผู้ปิดงวด ใช้ดูร่องรอยว่าใครปิดงวดใดเมื่อไร',
        },
        {
          kind: 'image',
          src: '/help/recon/08-history.png',
          caption: 'ประวัติการปิดงวด — รายการ statement ที่ปิดกระทบยอดแล้วทุกบัญชี พร้อมผู้ปิดงวด',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะ',
      blocks: [
        { kind: 'para', text: 'สถานะของ statement (งบกระทบยอด):' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['DRAFT', 'นำเข้าแล้ว ยังจับคู่/ปรับปรุง/เพิ่มรายการได้ — ยังไม่ปิด'],
            ['Reconciled', 'ปิดกระทบยอดแล้ว ถูกล็อก (แก้ไขไม่ได้)'],
          ],
        },
        { kind: 'para', text: 'สถานะของรายการ statement แต่ละบรรทัด:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รอจับคู่ (OPEN)', 'ยังไม่จับคู่กับใบสำคัญและยังไม่ปรับปรุง'],
            ['จับคู่แล้ว (MATCHED)', 'จับคู่กับใบสำคัญในระบบแล้ว'],
            ['ปรับปรุงแล้ว (ADJUSTED)', 'เคลียร์ด้วยรายการปรับปรุง'],
          ],
        },
        { kind: 'para', text: 'สถานะกระทบยอดของบัญชี (ในแท็บภาพรวม):' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ยังไม่ Import', 'บัญชีนี้ยังไม่ได้นำเข้า Bank Statement'],
            ['Reconciled', 'กระทบยอดเสร็จและปิดงวดแล้ว'],
            ['มีรายการค้าง', 'นำเข้าแล้วแต่ยังมีรายการรอจับคู่/ปรับปรุง'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'กดปุ่ม “ปิดยอด” แล้วปิดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้องผ่านครบทั้ง 3 ข้อในหน้าต่างปิดกระทบยอด: ไม่มีรายการค้าง (OPEN) · สมการยอดตรง · สถานะยังเป็น DRAFT',
            },
          ],
        },
        { kind: 'para', text: 'Auto-match แล้วยังเหลือรายการ?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'รายการที่จำนวน/วันที่ไม่ตรงเกณฑ์ หรือกำกวม จะไม่ถูกจับคู่ ให้ใช้ + เพิ่มรายการปรับปรุง เคลียร์ทีละรายการ (เช่น ค่าธรรมเนียม ดอกเบี้ย เช็คค้าง)',
            },
          ],
        },
        { kind: 'para', text: 'รายการปรับปรุงแบบใดที่ลงบัญชี (JV) ให้อัตโนมัติ?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ค่าธรรมเนียมธนาคาร · ดอกเบี้ยรับ · ผลต่างอัตราแลกเปลี่ยน จะลง JV ส่วนเช็คยังไม่ขึ้นเงิน · เงินฝากระหว่างทาง · อื่นๆ ไม่ลง JV',
            },
          ],
        },
        { kind: 'para', text: 'นำเข้าไฟล์แล้วกล่องสมการยอดเป็นสีส้ม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'แปลว่า ยอดยกมา + เคลื่อนไหว ไม่เท่ายอดปิดที่กรอก ให้ตรวจยอดยกมา/ยอดปิด หรือความครบถ้วนของรายการในไฟล์ก่อนนำเข้า',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Bank Statement — ',
              text: 'รายการเดินบัญชีที่ออกโดยธนาคาร (ยอดยกมา เคลื่อนไหว ยอดปิด)',
            },
            {
              lead: 'กระทบยอด (Reconciliation) — ',
              text: 'การเทียบยอด/รายการของธนาคารกับสมุดบัญชีในระบบให้ตรงกัน',
            },
            {
              lead: 'Auto-match — ',
              text: 'การจับคู่รายการ statement กับใบสำคัญอัตโนมัติ (จำนวนตรง + วันที่ ±3 วัน)',
            },
            {
              lead: 'รายการปรับปรุง (Adjustment) — ',
              text: 'รายการที่บันทึกเพื่อเคลียร์ผลต่าง เช่น ค่าธรรมเนียม ดอกเบี้ย เช็คค้าง',
            },
            {
              lead: 'เช็คยังไม่ขึ้นเงิน — ',
              text: 'เช็คที่จ่าย/รับแล้วแต่ยังไม่ตัดผ่านบัญชีธนาคาร (ไม่ลง JV)',
            },
            {
              lead: 'เงินฝากระหว่างทาง — ',
              text: 'เงินที่นำฝากแล้วแต่ธนาคารยังไม่บันทึกเข้าบัญชี (ไม่ลง JV)',
            },
            {
              lead: 'ปิดกระทบยอด (Finalize) — ',
              text: 'การล็อก statement เป็น RECONCILED เมื่อทุกรายการเคลียร์และสมการยอดตรง',
            },
          ],
        },
      ],
    },
  ],
};
